import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import AdminEditDialog from "../components/AdminEditDialog";
import PhotoGallery from "../components/PhotoGallery";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft, Circle, ShoppingCart, Plus, Minus, ArrowsClockwise,
  WarningDiamond, ClipboardText, Gauge, Wrench,
} from "@phosphor-icons/react";

const EVENT_ICON = {
  purchase: ShoppingCart, fitment: Plus, removal: Minus, rotation: ArrowsClockwise,
  puncture: WarningDiamond, inspection: ClipboardText, pressure: Gauge, repair: Wrench, retread: ArrowsClockwise,
};
const EVENT_COLOR = {
  purchase: "text-sky-400", fitment: "text-emerald-400", removal: "text-zinc-400",
  rotation: "text-teal-400", puncture: "text-rose-400", inspection: "text-amber-400",
  pressure: "text-violet-400", repair: "text-fuchsia-400", retread: "text-fuchsia-400",
};
const FITMENT_COLS = [["Dumper", "dumper_number"], ["Position", "position"], ["Condition", "condition"], ["Engine Hrs", "engine_hours"]];
const REMOVAL_COLS = [["Reason", "reason"], ["Hrs Done", "hours_completed"], ["Engine Hrs", "engine_hours"], ["Tread", "tread_depth"]];
const ROTATION_COLS = [["To Dumper", "new_dumper_number"], ["Position", "new_position"], ["Engine Hrs", "engine_hours"]];
const INSPECTION_COLS = [["Action", "recommended_action"], ["Tread", "tread_depth"], ["Pressure", "pressure"], ["Engine Hrs", "engine_hours"]];
const PRESSURE_COLS = [["Actual", "actual_pressure"], ["Recommended", "recommended_pressure"], ["Diff", "difference"]];
const PUNCTURE_COLS = [["Report", "report_number"], ["Type", "puncture_type"], ["Cost", "repair_cost"], ["Engine Hrs", "engine_hours"]];

function Metric({ label, value, mono = true }) {
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-950 p-3">
      <div className="overline text-zinc-500">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono tabular-nums" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

const fmtVal = (v) => (v == null || v === "" ? "—" : (typeof v === "number" ? Math.round(v).toLocaleString() : String(v)));

function HistoryList({ title, rows, cols }) {
  return (
    <div>
      <div className="overline mb-2 text-zinc-500">{title} ({rows.length})</div>
      {rows.length === 0 ? <p className="text-xs text-zinc-500">None recorded.</p> : (
        <div className="overflow-x-auto rounded-sm border border-zinc-800">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950 text-left text-zinc-500"><tr>
              {cols.map((c) => <th key={c[1]} className="px-2 py-1.5">{c[0]}</th>)}
              <th className="px-2 py-1.5">Date</th>
            </tr></thead>
            <tbody className="divide-y divide-zinc-800">
              {rows.map((r) => (
                <tr key={r.id} className="bg-zinc-900">
                  {cols.map((c) => <td key={c[1]} className="px-2 py-1.5 tabular-nums">{fmtVal(r[c[1]])}</td>)}
                  <td className="px-2 py-1.5 text-zinc-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TyrePassport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get(`/tyres/${id}/passport`).then(({ data }) => setData(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const t = data.tyre;
  const m = t.metrics || {};

  const photoGroups = [
    ...data.fitments.map((f) => ({ label: `Fitment — ${f.dumper_number || ""} ${f.position || ""}`, date: f.fitment_date || f.created_at, photos: f.photos })),
    ...data.removals.map((r) => ({ label: `Removal — ${r.reason || ""}`, date: r.removal_date || r.created_at, photos: r.photos })),
    ...data.inspections.map((x) => ({ label: `Inspection — ${x.recommended_action || ""}`, date: x.inspection_date || x.created_at, photos: x.photos })),
    ...data.punctures.map((p) => ({ label: `${p.report_number || "Puncture"} — ${p.puncture_type || ""}`, date: p.created_at, photos: [...(p.photos_before || []), ...(p.photos_after || [])] })),
  ].filter((g) => (g.photos || []).some((ph) => ph && ph.id));

  return (
    <div className="space-y-6">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-amber-500">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="overline text-amber-500 flex items-center gap-2"><Circle size={14} /> Tyre Passport</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t.serial_number}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <StatusBadge status={t.status} />
            <StatusBadge status={m.tread_status} />
            <span className="text-sm text-zinc-400">{t.size} · {t.vehicle_type} · {t.condition}</span>
          </div>
        </div>
        {hasRole?.("admin") && (
          <AdminEditDialog
            title={`Edit Tyre — ${t.serial_number}`}
            endpoint={`/tyres/${t.id}`}
            testid="edit-tyre"
            onSaved={load}
            initial={{ serial_number: t.serial_number, size: t.size, status: t.status,
              current_position: t.current_position, condition: t.condition,
              initial_tread_depth: t.initial_tread_depth, current_tread_depth: t.current_tread_depth,
              current_pressure: t.current_pressure, recommended_pressure: t.recommended_pressure,
              fitment_hours: t.fitment_hours, fitment_km: t.fitment_km,
              total_hours: t.total_hours, total_km: t.total_km,
              purchase_cost: t.purchase_cost, repair_cost: t.repair_cost, retread_cost: t.retread_cost }}
            fields={[
              { name: "serial_number", label: "Serial Number" },
              { name: "size", label: "Size" },
              { name: "status", label: "Status (Fitted / In Stock / Removed / Under Repair)" },
              { name: "current_position", label: "Current Position" },
              { name: "condition", label: "Condition" },
              { name: "initial_tread_depth", label: "Initial Tread (mm)", type: "number" },
              { name: "current_tread_depth", label: "Current Tread / NSD (mm)", type: "number" },
              { name: "current_pressure", label: "Current Pressure (psi)", type: "number" },
              { name: "recommended_pressure", label: "Recommended Pressure (psi)", type: "number" },
              { name: "fitment_hours", label: "Fitment Engine Hours", type: "number" },
              { name: "fitment_km", label: "Fitment KM", type: "number" },
              { name: "total_hours", label: "Total Hours", type: "number" },
              { name: "total_km", label: "Total KM", type: "number" },
              { name: "purchase_cost", label: "Purchase Cost (₹)", type: "number" },
              { name: "repair_cost", label: "Repair Cost (₹)", type: "number" },
              { name: "retread_cost", label: "Retread Cost (₹)", type: "number" },
            ]}
          />
        )}
      </div>

      {m.initial_tread_depth ? (
        <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5">
          <div className="mb-2 flex justify-between text-sm">
            <span className="text-zinc-400">Tread Life — {m.current_tread_depth}mm / {m.initial_tread_depth}mm</span>
            <span className="font-mono tabular-nums text-amber-500">{m.pct_tread_consumed}% consumed</span>
          </div>
          <Progress value={Math.min(100, m.pct_tread_consumed || 0)} className="h-2" />
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-4">
        <Metric label="Total Hours" value={Math.round(m.total_hours || 0).toLocaleString()} />
        <Metric label="Total KM" value={Math.round(m.total_km || 0).toLocaleString()} />
        <Metric label="CPKM (₹/km)" value={m.cost_per_km != null ? `₹${m.cost_per_km.toLocaleString()}` : "—"} />
        <Metric label="Total Cost" value={m.total_cost != null ? `₹${Math.round(m.total_cost).toLocaleString()}` : "—"} />
        <Metric label="Projected Life (km)" value={m.projected_life_km != null ? m.projected_life_km.toLocaleString() : "—"} />
        <Metric label="Projected CPKM" value={m.projected_cost_per_km != null ? `₹${m.projected_cost_per_km.toLocaleString()}` : "—"} />
        <Metric label="Current Dumper" value={t.current_vehicle?.fleet_number || "In Stock"} mono={false} />
        <Metric label="Position" value={t.current_position || "—"} mono={false} />
        <Metric label="Fitment KM" value={m.fitment_km != null ? Math.round(m.fitment_km).toLocaleString() : "—"} />
        <Metric label="KM This Fitment" value={Math.round(m.km_delivered_current || 0).toLocaleString()} />
        <Metric label="Recommended PSI" value={m.recommended_pressure ?? "—"} />
        <Metric label="Current PSI" value={m.current_pressure ?? "—"} />
        <Metric label="Punctures" value={m.puncture_count} />
        <Metric label="Repairs" value={m.repair_count} />
      </div>

      <div className="rounded-sm border border-zinc-800 border-l-2 border-l-amber-500 bg-zinc-900 p-5 ring-1 ring-white/5">
        <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Lifecycle Timeline</h2>
        <div className="space-y-0">
          {[...data.events].reverse().map((e, i) => {
            const Icon = EVENT_ICON[e.event_type] || Circle;
            return (
              <div key={e.id} data-testid={`event-${i}`} className="flex gap-3 pb-4">
                <div className="flex flex-col items-center">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full bg-zinc-800 ring-2 ring-zinc-950 ${EVENT_COLOR[e.event_type]}`}>
                    <Icon size={16} weight="bold" />
                  </div>
                  {i < data.events.length - 1 && <div className="mt-1 w-0.5 flex-1 bg-zinc-800" />}
                </div>
                <div className="pb-2">
                  <div className="text-sm font-semibold">{e.description}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                    <span className="overline text-zinc-500">{new Date(e.timestamp).toLocaleDateString()}</span>
                    {e.meta?.engine_hours != null && (
                      <span className="font-mono font-semibold text-amber-500">@ {Math.round(e.meta.engine_hours).toLocaleString()} engine hrs</span>
                    )}
                    {e.meta?.hours != null && (
                      <span className="font-mono text-zinc-400">{Math.round(e.meta.hours).toLocaleString()} hrs on tyre</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {data.events.length === 0 && <p className="text-sm text-zinc-500">No events recorded.</p>}
        </div>
      </div>

      <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5">
        <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Complete History</h2>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <HistoryList title="Fitments / Refits" rows={data.fitments} cols={FITMENT_COLS} />
          <HistoryList title="Removals" rows={data.removals} cols={REMOVAL_COLS} />
          <HistoryList title="Rotations" rows={data.rotations} cols={ROTATION_COLS} />
          <HistoryList title="Inspections" rows={data.inspections} cols={INSPECTION_COLS} />
          <HistoryList title="Pressure Checks" rows={data.pressures} cols={PRESSURE_COLS} />
          <HistoryList title="Punctures" rows={data.punctures} cols={PUNCTURE_COLS} />
        </div>
      </div>

      {(data.tread_readings || []).length > 0 && (
        <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5" data-testid="tread-history">
          <h2 className="mb-1 font-display text-lg font-bold tracking-tight">Tread Wear History</h2>
          <p className="mb-4 text-xs text-zinc-500">Tread depth recorded over time (from inspections & removals).</p>
          <div className="overflow-x-auto rounded-sm border border-zinc-800">
            <table className="w-full text-xs">
              <thead className="bg-zinc-950 text-left text-zinc-500"><tr>
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Tread (mm)</th><th className="px-3 py-2">Source</th>
              </tr></thead>
              <tbody className="divide-y divide-zinc-800">
                {data.tread_readings.map((r) => (
                  <tr key={r.id} className="bg-zinc-900">
                    <td className="px-3 py-2 text-zinc-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-amber-500">{r.tread_depth}</td>
                    <td className="px-3 py-2 text-zinc-400 capitalize">{r.source || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {photoGroups.length > 0 && (
        <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5" data-testid="passport-photos">
          <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Photos ({photoGroups.reduce((n, g) => n + g.photos.filter((p) => p && p.id).length, 0)})</h2>
          <p className="mb-4 text-xs text-zinc-500">Tap any photo to enlarge.</p>
          <div className="space-y-4">
            {photoGroups.map((g, i) => (
              <div key={`pg-${i}`}>
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span className="font-semibold text-zinc-300">{g.label}</span>
                  <span className="text-zinc-500">{g.date ? new Date(g.date).toLocaleDateString() : ""}</span>
                </div>
                <PhotoGallery photos={g.photos} testid={`passport-photo-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
